import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  Order,
  MedicalTest,
  User,
  FilterDetails,
  FAQCategory,
  NavigationDetails,
  OrderStatus,
  Counter,
  Partner,
  OtherServices,
  Search,
  Drug,
  CartQuantityItem,
  Testimonial,
  CommonData,
  QuantityDetails,
  CartCostOptions
} from '../models';
import Swal from 'sweetalert2';
import { 
  AuthenticationService, 
  CartService, 
  MedicalTestService,
  PharmacyService
 } from './index';
import { Router } from '@angular/router';
import { first } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class DataService {
  filteredProductsSubject = new BehaviorSubject<MedicalTest[] | Drug[]>([]);
  allLocalMedicalTestsSubject = new BehaviorSubject<MedicalTest[]>([]);
  allLocalDrugsSubject = new BehaviorSubject<Drug[]>([]);
  allQuantityNamesSubject = new BehaviorSubject<QuantityDetails[]>([]);
  cartItemsSubject = new BehaviorSubject<MedicalTest[] | Drug[]>([]);
  categoryLabelsSubject = new BehaviorSubject<string[]>([]);
  drugSearchCountryFiltersSubject = new BehaviorSubject<string[] | null>([]);
  selectedCountryFiltersSubject = new BehaviorSubject<string[]>([]);
  orderToEditSubject = new BehaviorSubject<Order | null>(null);
  productToEditSubject = new BehaviorSubject<MedicalTest | Drug | null>(null);
  dataToViewSubject = new BehaviorSubject<MedicalTest | Drug | Order | OtherServices | null>(null);
  medicalSearchCriteria = new BehaviorSubject<Search | null>(null);
  drugSearchCriteria = new BehaviorSubject<Search | null>(null);
  addConsultationSubject = new BehaviorSubject<boolean>(false);
  cartItemsQuantitiesSubject = new BehaviorSubject<CartQuantityItem[]>([]);
  medicalConsultationAmountSubject = new BehaviorSubject<number>(0);
  page: number = 1;
  stateOfFilter: boolean[] = [];
  medicalTests: MedicalTest[] = [];
  consultationAmount: number;
  cartItemsQuantities: CartQuantityItem[] = [];
  productQuantity: { [id: number]: number } = {};
  cartItemQuantitiesAdded: CartQuantityItem[] = [];
  cartItems: MedicalTest[] | Drug[] = [];
  cartUpdatedSubject = new BehaviorSubject<void>(undefined);
  cartUpdatedBadgeCounter = this.cartUpdatedSubject.asObservable();
  recommendedMedicalTestsSubject = new BehaviorSubject<MedicalTest[]>([]);
  recommendedDrugsSubject = new BehaviorSubject<Drug[]>([]);
  selectedDrugQuantities: { [key: number]: QuantityDetails } = {};
  selectedQuantityTypeSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private authenticationService: AuthenticationService,
    private cartService: CartService,
    private medicalTestService: MedicalTestService,
    private pharmacyService: PharmacyService,
    private router: Router,
  ) { }

  /**
   * Saves the filtered products
   * @param medicalTest/drug filtered
   */
  saveFilteredProducts(product: MedicalTest[] | Drug[]) {
    this.filteredProductsSubject.next(product);
  }

  /**
   * Saves medical tests
   * @param medicalTests
   */
  saveAllLocalMedicalTests(medicalTests: MedicalTest[]) {
    this.allLocalMedicalTestsSubject.next(medicalTests);
  }

  /**
   * Saves drugs
   * @param drugs
   */
  saveAllLocalDrugs(drugs: Drug[]) {
    this.allLocalDrugsSubject.next(drugs);
  }

  /**
   * Saves drug quantity names
   * @param quantityNames
   */
  saveAllLocalQuantityNames(quantityNames: QuantityDetails[]) {
    this.allQuantityNamesSubject.next(quantityNames);
  }

  /**
   * Saves all the cart items
   * @param cartItem filtered
   */
  updateCartItems(cartItems: MedicalTest[] | Drug[]) {
    this.cartItemsSubject.next(cartItems);
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
  }

  /**
   * Saves the category labels of the medical tests
   * @param labels to be save
   */
  saveLocalCategoryLabels(labels: string[]) {
    this.categoryLabelsSubject.next(labels);
  }

  /**
   * Saves the country filters of a broad drug search
   * @param countries to be saved
   */
  saveLocalCountryFilters(countries: string[]) {
    this.drugSearchCountryFiltersSubject.next(countries);
  }

  /**
   * Saves the countries slected for an advanced search
   * @param countries to be saved
   */
  saveLocalSelectedCountriesFilters(countries: string[]) {
    this.selectedCountryFiltersSubject.next(countries);
  }

  /**
   * Saves the order to edit
   * @param order to be save
   */
  saveLocalOrderToEdit(order: Order) {
    this.orderToEditSubject.next(order);
  }

  /**
   * Saves the product to edit
   * @param product to be saved
   */
  saveProductToEdit(product: MedicalTest | Drug) {
    this.productToEditSubject.next(product);
  }

  /**
   * Saves the data that the user is going to view
   * @param data to be saved
   */
  saveLocalDataToView(data: MedicalTest | Drug | Order | OtherServices) {
    this.dataToViewSubject.next(data);
  }

  /**
   * Saves the form data for the search
   * @param searchCriteria to be saved
   */
  // saveLocalSearchCriteria(searchCriteria: Search | null) {
  //   this.searchSourceSubject.next(searchCriteria);
  // }
  saveLocalSearchCriteria(searchCriteria: Search | null, searchedComponent: 'drugs' | 'medicalTests') {
    if (searchedComponent === 'drugs') {
      this.drugSearchCriteria.next(searchCriteria);
    } else if (searchedComponent === 'medicalTests') {
      this.medicalSearchCriteria.next(searchCriteria);
    }
  }

  /**
   * Saves medical consultation in the cart
   * @param status to be saved
   */
  saveLocalAddConsultationStatus(status: boolean) {
    this.addConsultationSubject.next(status);
  }

  /**
   * Saves all the product id, quantities, type, and amount being added to the cart
   * @param quantities added to cart
   */
  saveLocalCartItemQuantities(quantities: CartQuantityItem[]) {
    localStorage.setItem('cartItemQuantities', JSON.stringify(quantities));
    this.cartItemsQuantitiesSubject.next(quantities);
  }

  /**
   * Saves the medical consultation amount
   * @param amount of consultation
   */
  saveLocalMedicalConsultationAmount(amount: number) {
    this.medicalConsultationAmountSubject.next(amount);
  }

  /**
   * Saves the selected quantity type 
   * @param value of quantity type to be saved
   */
  saveSelectedQuantityType(value: string | null): void {
    this.selectedQuantityTypeSubject.next(value);
  }

  /**
   * Gets the filtered Meddical tests
   * @return Observable of array of medical tests
   */
  getFilteredProducts() {
    return this.filteredProductsSubject.asObservable();
  }

  /**
   * Gets the all medicalTests
   * @return Observable of array of medicalTests
   */
  getAllLocalMedicalTests() {
    return this.allLocalMedicalTestsSubject.asObservable();
  }

  /**
   * Gets the all drugs
   * @return Observable of array of drugs
   */
  getAllLocalDrugs() {
    return this.allLocalDrugsSubject.asObservable();
  }

  /**
   * Gets the all drug quantity names
   * @return Observable of array of quantity names
   */
  getAllLocalQuantityNames() {
    return this.allQuantityNamesSubject.asObservable();
  }

  /**
   * Gets the all cart items
   * @return Observable of array of medical tests or drugs
   */
  getCartItems() {
    return this.cartItemsSubject.asObservable();
  }

  /**
   * Gets the location labels of the Jobs
   * @return Observable of array of location labels
   */
  getLocalCategoryLabels() {
    return this.categoryLabelsSubject.asObservable();
  }

  /**
   * Gets the country filters of the drug search
   * @return Observable of array of country filters labels
   */
  getLocalCountryFilters() {
    return this.drugSearchCountryFiltersSubject.asObservable();
  }

  /**
   * Gets the selected countries filters of the drug advanced search
   * @return Observable of array of country filters labels
   */
  getLocalSelectedCountriesFilters() {
    return this.selectedCountryFiltersSubject.asObservable();
  }

  /**
   * Gets the order to edit
   * @return Observable of the order to edit
   */
  getLocalOrderToEdit() {
    return this.orderToEditSubject.asObservable();
  }

  /**
   * Gets the product to edit
   * @return Observable of the product to edit
   */
  getProductToEdit() {
    return this.productToEditSubject.asObservable();
  }

  /**
   * Gets all product quantities
   * @return Observable of array of products
   */
  getLocalCartItemQuantities(): Observable<CartQuantityItem[]> {
    const quantities = JSON.parse(localStorage.getItem('cartItemQuantities') || '[]');
    this.cartItemsQuantitiesSubject.next(quantities);
    return this.cartItemsQuantitiesSubject.asObservable();
  }
  
  getExistingLocalCartItemQuantities(): CartQuantityItem[] {
    return JSON.parse(localStorage.getItem('cartItemQuantities') || '[]');
  }

  /**
   * Gets medical consultation amount
   * @return Observable of consultation amount
   */
  getLocalMedicalConsultationAmount() {
    return this.medicalConsultationAmountSubject.asObservable();
  }

  /**
   * Gets the quantity type selected
   * @return Observable of the selected type
   */
  getSelectedQuantityType() {
    return this.selectedQuantityTypeSubject.asObservable();
  }

  /**
   * Clear the saved medical test
   * 
   */
  clearProduct() {
    this.productToEditSubject.next(null); 
  }

  /**
   * Gets the data to view
   * @return Observable of the data to view
   */
  getLocalDataToView() {
    return this.dataToViewSubject.asObservable();
  }

  /**
   * Gets the data to view
   * @return Observable of the data to view
   */
  // getSearchCriteria() {
  //   return this.searchSourceSubject.asObservable();
  // }
  getSearchCriteria(searchedComponent: 'drugs' | 'medicalTests') {
    if (searchedComponent === 'drugs') {
      return this.drugSearchCriteria.asObservable();
    } else {
      return this.medicalSearchCriteria.asObservable();
    }
  }

  /**
   * Gets the status value
   * @return Observable of the boolean saved
   */
  getLocalConsultationStatus() {
    return this.addConsultationSubject.asObservable();
  }

  addRemoveCartItem(cartAddedItem: MedicalTest | Drug, cartItems: MedicalTest[] | Drug[]) {
    const index = cartItems.findIndex(item => item.id === cartAddedItem.id);
    if (index !== -1) {
      this.cartService.removeFromCart(cartAddedItem);
    } else {
      this.cartService.saveToCart([cartAddedItem]);
      this.updateRecommendedProducts(cartAddedItem);
    }
  }

  updateRecommendedProducts(product: MedicalTest | Drug) {
    const formData = new FormData();
    if ('country_of_origin' in product) {
      formData.append('productIds[]', product.id.toString());
      this.pharmacyService.getRecommendedDrugs(formData).pipe(first())
        .subscribe(response => {
          this.recommendedDrugsSubject.next(response as Drug[]); 
        });
    } else {
      formData.append('productIds[]', product.id.toString());
      this.medicalTestService.getRecommendedMedicalTests(formData).pipe(first())
        .subscribe(response => {
          this.recommendedMedicalTestsSubject.next(response as MedicalTest[]);
        });
    }
  }

  getRecommendedMedicalTests(): Observable<MedicalTest[]> {
    return this.recommendedMedicalTestsSubject.asObservable();
  }

  getRecommendedDrugs(): Observable<Drug[]> {
    return this.recommendedDrugsSubject.asObservable();
  }

  filterMedicalTests(searchCriteria: Search, index?: number) {
    this.page = 1;
    if (index) {
      this.stateOfFilter = [];
      this.stateOfFilter[index] = !this.stateOfFilter[index];
    }
    return this.medicalTestService.getFilteredProducts(searchCriteria)
  }

  filterDrugs(searchCriteria: Search, index?: number) {
    this.page = 1;
    if (index) {
      this.stateOfFilter = [];
      this.stateOfFilter[index] = !this.stateOfFilter[index];
    }
    // Convert searchCriteria to FormData
    const formData = new FormData();
    Object.entries(searchCriteria).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value as any);
      }
    });
    return this.pharmacyService.getFilteredDrugs(formData);
  }

  /**
   * Gets the filter details
   * @return NavigationDetails[] of the links
   */
  getLocalNavigationDetails(): NavigationDetails[] {
    return [
      {
        "link": "/",
        "linkText": "Home"
      },
      {
        "link": "/dashboard",
        "linkText": "Dashboard"
      },
      {
        "link": "/admin",
        "linkText": "Admin"
      },
      {
        "link": "/medical-tests",
        "linkText": "Medical Tests"
      },
      {
        "link": "/pharmacy",
        "linkText": "Pharmacy"
      },
      {
        "link": "/our-services",
        "linkText": "Services"
      },
      {
        "link": "/about",
        "linkText": "About Us"
      },
      {
        "link": "/contact",
        "linkText": "Contact Us"
      }
    ];
  }

  /**
   * Gets the status details
   * @return OrderStatus[] of statuses
   */
  getLocalOrderStatus(): OrderStatus[] {
    return [
      {
        status: 'Order Confirmation',
        description: 'Your order has been placed successfully.',
      },
      {
        status: 'Sample Collection',
        description: 'Your sample has been collected.',
      },
      {
        status: 'Sample Processing',
        description: 'Your sample is being processed.',
      },
      {
        status: 'Results Processing',
        description: 'Your results are being processed.',
      },
      {
        status: 'Order Complete',
        description: 'This order has been completed.',
      }
    ] as OrderStatus[];
  }

  /**
   * Gets the counter details
   * @return Counter[] of counters
   */
  getCounter(): Counter[] {
    return [
      {
        icon: 'fal fa-procedures',
        title: 'Happy Customers',
        value: 0,
      },
      {
        icon: 'al fa-file-medical-alt',
        title: 'Tests Done',
        value: 0,
      },
      {
        icon: 'fal fa-heartbeat',
        title: 'Current Locations',
        value: 13,
      }
    ] as Counter[];
  }

  /**
   * Gets the partner details
   * @return Partner[] of partners
   */
  getLocalPartners(): Partner[] {
    return [
      {
        name: 'Lancent Laboratories',
        image: 'assets/img/partner-lancent-logo.png'
      },
      {
        name: 'Care IHK',
        image: 'assets/img/care.png'
      },
      // Removed per client's request
      // {
      //   name: 'Joint Medical Stores',
      //   image: 'assets/img/JMS_logo.png'
      // },
      {
        name: 'Mesh Innovation Centre',
        image: 'assets/img/mesh-logo.png'
      },
      {
        name: 'Rist Pharmacy',
        image: 'assets/img/RIST.png'
      },
    ] as Partner[];
  }

  /**
   * Gets the location option labels
   * @return an array of labels
   */
  getLocalLocationLabels() {
    return [
      'Kampala',
      'Mukono',
      'Entebbe',
      'Kawempe',
      'Jinja',
      'Masaka',
      'Mbarara',
      'Ishaka',
      'Mbale',
      'Fort Portal',
    ];
  }

  /**
   * Gets the filter details
   * @return FilterDetails[] of medical tests
   */
  getLocalFilterDetails(): FilterDetails[] {
    return [
      {
        title: 'Common Medical Tests',
        icon: 'fal fa-disease',
      },
      {
        title: 'Basic Health Checkups',
        icon: 'fal fa-dumbbell',
      },
      {
        title: 'Routine Blood Tests',
        icon: 'fal fa-tint',
      },
      {
        title: 'Infectious Diseases',
        icon: 'fal fa-biohazard',
      },
      {
        title: 'Nutritional and Metabolic Panels',
        icon: 'fal fa-stomach',
      },
      {
        title: 'Cancer Screenings',
        icon: 'fal fa-disease',
      },
      {
        title: 'Genetic Testing',
        icon: 'fal fa-dna',
      },
      {
        title: 'Hormone Levels',
        icon: 'fal fa-vial',
      },
      {
        title: 'Allergy and Sensitivity Testing',
        icon: 'fal fa-allergies',
      },
      {
        title: 'Cardio Health',
        icon: 'fal fa-heart-rate',
      },
      {
        title: 'Diabetes Monitoring',
        icon: 'fal fa-tachometer-fastest',
      },
      {
        title: 'Renal (Kidney) Function Tests',
        icon: 'fal fa-kidneys',
      },
      {
        title: 'Liver Function Tests:',
        icon: 'fal fa-vials',
      },
      {
        title: 'Thyroid Function Tests',
        icon: 'fal fa-diploma',
      },
      {
        title: 'Immunology Tests',
        icon: 'fal fa-prescription-bottle',
      },
      {
        title: 'Microbiology Tests',
        icon: 'fal fa-microscope',
      },
      {
        title: 'Fertility and Reproductive Health',
        icon: 'fal fa-venus-double',
      },
      {
        title: 'Drug and Alcohol Testing',
        icon: 'fal fa-wine-bottle',
      },
      {
        title: 'Occupational Health Screenings',
        icon: 'fal fa-hospital-user',
      },
      {
        title: 'Travel Health Screenings',
        icon: 'fal fa-plane-departure',
      },
      {
        title: 'Others',
        icon: 'fal fa-books-medical',
      }
    ];
  }

   /**
   * Gets the other services details
   * @return OtherServices[] of services
   */
   getOtherLocalServices(): OtherServices[] {
    return [
      {
        title: 'Ask The Doctor',
        images: [
          'assets/img/book-consultation.jpg',
          'assets/img/book-consultation1.jpg',
          'assets/img/book-consultation2.jpg',
        ],
        icon: 'flaticon-stethoscope',
        introduction: 'Take charge of your health with ease! Use Go-Checks to connect with licensed doctors anytime.',
        description: `<p> Take charge of your health with ease! Use Go-Checks to connect with licensed doctors anytime, anywhere. Whether you need advice, a prescription, or a follow-up, our telemedicine platform makes it simple and fast. </p>
        <h5>How It Works:</h5>
        <ol>
          <li>
            <b>Log in to Your Portal</b></br>
            Sign in to your personal health portal using your unique ID. If you don’t have an account yet, you can easily create one by signing up on our website.
          </li>
          <li>
            <b>Add medical consultation to cart</b></br>
            Under the section of “Book medical consultation”
          </li>
          <li>
            <b>Pick a Date</b></br>
            Schedule a consultation on Date that’s convenient for you. Our team will call you to confirm the date and time, an online meeting will also be shared with you.
          </li>
          <li>
            <b>Join Your Session</b></br>
            Meet your doctor online via a secure video or voice call.
          </li>
        </ol>
        <p>Start your journey to better health today!</p>`
      },
      {
        title: 'Order For Drugs',
        images: [
          'assets/img/order-drugs.jpg',
          'assets/img/order-drugs1.jpg',
          'assets/img/order-drugs2.jpg',
        ],
        icon: 'flaticon-doctor-1',
        introduction: 'Skip the pharmacy lines and get your medications delivered straight to your doorstep!',
        description: `<p> Skip the pharmacy lines and get your medications delivered straight to your doorstep! Go-Checks makes ordering drugs quick, secure, and convenient. </p>
        <h5>Why Choose Us?</h5>
        <ul>
          <li>
            <b>Wide Selection</b></br>
            Access a comprehensive range of prescription and over-the-counter medications.
          </li>
          <li>
            <b>Affordable Prices</b></br>
            Enjoy competitive prices with no hidden fees.
          </li>
          <li>
            <b>Fast Delivery</b></br>
            Receive your medications at your home or office in record time.
          </li>
          <li>
            <b>100% Authentic</b></br>
            We partner with trusted pharmacies to ensure quality and safety.
          </li>
        </ul>
        <h5>How to Order:</h5>
        <ol>
          <li>
            <b>Log in to Your Portal</b></br>
            Sign in to your personal health portal using your unique ID. If you don’t have an account yet, you can easily create one by signing up on our website.
          </li>
          <li>
            <b>Choose Your Medications</b></br>
            Browse and add the items to your cart. Make a Payment: Pay securely using mobile money.
          </li>
          <li>
            <b>Sit Back and Relax</b></br>
            We’ll deliver your order to your preferred location.
          </li>
        </ol>
        <p>Your health, delivered with care!</p>`
      },
      {
        title: 'Home Doctor Visit',
        images: [
          'assets/img/home-doctor-visit.jpg',
          'assets/img/home-doctor-visit1.jpg',
          'assets/img/home-doctor-visit2.jpg',
          'assets/img/home-doctor-visit3.jpg',
        ],
        icon: 'flaticon-heartbeat',
        introduction: 'Healthcare, right at your doorstep! With Go-Checks, you can now book a doctor to visit at your home or office.',
        description: `
        <p> Healthcare, right at your doorstep! With Go-Checks, you can now book a doctor to visit you in the comfort of your home. Whether it’s a routine check-up, follow-up care, or urgent medical attention, we’ve got you covered.</p>
        <h5>Benefits of Home Doctor Visits</h5>
        <ul>
          <li>
            <b>Convenience</b></br>
            No need to leave your home — our doctors come to you.
          </li>
          <li>
            <b>Personalized Care</b></br>
            Enjoy one-on-one attention tailored to your health needs.
          </li>
          <li>
            <b>Safe and Comfortable</b></br>
            Perfect for elderly patients, children, or those with mobility challenges.
          </li>
          <li>
            <b>Time-Saving</b></br>
            Avoid the hassle of traffic and waiting rooms.
          </li>
        </ul>
        <h5>How It Works:</h5>
        <ol>
          <li>
            <b>Log in to Your Portal</b></br>
            Sign in to your personal health portal using your unique ID. If you don’t have an account yet, you can easily create one by signing up on our website.
          </li>
          <li>
            <b>Request Visit</b></br>
            Under the section of “Home doctor visit”
          </li>
          <li>
            <b>Pick a Date</b></br>
            Schedule Date that’s convenient for you. Our team will call you to confirm the date and time.
          </li>
          <li>
            <b>Relax and Recover</b></br>
            Our doctors will provide professional care at your location.
          </li>
        </ol>
        <p>Get professional healthcare without leaving home!</p>`
      },
      {
        title: 'Request a Lab Test',
        images: [
          'assets/img/about-2.jpeg',
          'assets/img/request-lab.jpg',
          'assets/img/request-lab1.jpg',
          'assets/img/request-lab2.jpg',
        ],
        icon: 'flaticon-medical-record',
        introduction: 'Take control of your health by requesting lab tests tailored to your needs, anywhere, anytime.',
        description: `<p> Take control of your health by requesting lab tests tailored to your needs. 
        Our platform offers a convenient and seamless way to schedule lab tests that help monitor your health, 
        diagnose conditions, and track your progress. Whether you're managing a chronic condition or just looking to stay proactive, 
        we make the testing process simple, fast, and secure.</p>
        <h5>How to Request a Lab Test:</h5>
        <ol>
          <li>
            <b>Log in to Your Portal</b></br>
            Sign in to your personal health portal using your unique ID. If you don’t have an account yet, you can easily create one by signing up on our website.
          </li>
          <li>
            <b>Choose Your Test</b></br>
            Browse through the available tests based on your health needs. You can request common screenings for Diabetes, Hypertension, cholesterol, and more. 
            If you're unsure which test to choose, you can consult with our healthcare professionals for guidance.
          </li>
          <li>
            <b>Select a Location or Home Service</b></br>
            Choose a convenient location for your test, whether it’s a clinic or a home service. Our home testing service allows you to have a certified professional 
            visit your location for sample collection at your convenience.
          </li>
          <li>
            <b>Schedule Your Appointment</b></br>
            Pick a date that fits your schedule. We offer flexible hours to ensure that you can get tested at your convenience.
          </li>
          <li>
            <b>Receive Your Results</b></br>
            Once your lab test is completed, your results will be securely uploaded to your email address. You’ll be notified when your results are ready, 
            and you can review them at any time. If necessary, our healthcare professionals will follow up with you for further consultation or treatment.
          </li>
          <li>
            <b>Follow-Up Care</b></br>
            Based on your test results, our team of doctors can provide follow-up care, recommend treatments, or suggest lifestyle changes to help you manage your health.
          </li>
        </ol>
        <p>Get professional healthcare without leaving home!</p>`
      }
    ] as OtherServices[];
  }

  /**
   * Gets the testimonials details
   * @return Testimonial[] 
   */
  getLocalTestimonials(): Testimonial[] {
    return [
      {
        name: 'Marvin Ssempijja',
        role: 'Pharmacist',
        message: 'This is a great platform, I like the services offered especially the packages that come in full set.',
        image: 'assets/img/avatar.png'
      },
      {
        name: 'Jimmy Nsanja',
        role: 'Accountant',
        message: 'I am incredibly impressed with the quality and efficiency of the service. Highly recommended!',
        image: 'assets/img/avatar.png'
      },
      {
        name: 'Audrey Nabulya',
        role: 'Pharmacist',
        message: 'Fantastic experience! The team went above and beyond to ensure everything was perfect.',
        image: 'assets/img/avatar.png'
      },
      {
        name: 'Emily Uwase',
        role: 'Small Business Owner',
        message: 'The customer support was outstanding, and I felt valued as a client. Would definitely use again!',
        image: 'assets/img/avatar.png'
      }
    ] as Testimonial[];
  }

  /**
   * Gets the FAQs
   * @return FAQCategory[] of FAQs
  */
  getLocalFaqs(): FAQCategory[] {
    return [
      {
        id: 1,
        title: "General Information",
        items: [
          {
            title: "How do I know if I need a screening for a disease?",
            text: "If you have a family history of health conditions, are over 40 years old, or have abnormal symptoms, it’s a good idea to schedule a screening for that disease. Regular screenings help in early detection and prevention."
          },
          {
            title: "Are the screenings accurate?",
            text: "Yes, our screenings are conducted by certified healthcare professionals using high-quality equipment and methods. Your results will be monitored by experienced physicians to ensure accuracy and reliability."
          },
          {
            title: "Can I use your platform if I already have a diagnosis?",
            text: "Absolutely! Our platform helps you monitor your condition over time, receive personalized care plans, and connect with physicians to manage your treatment effectively."
          },
        ]
      },
      {
        id: 2,
        title: "Account & Access",
        items: [
          {
            title: "What happens if I forget my password?",
            text: "If you forget your password, click on the “Forgot Password” link on the login page. You’ll be prompted to enter your registered email address to reset your password securely."
          },
          {
            title: "Can I update my personal information after registration?",
            text: "Yes, you can update your personal details anytime by logging into your portal and making the necessary changes. Make sure your contact information is always up-to-date to receive notifications."
          },
          {
            title: "Is my personal data secure on the platform?",
            text: "Yes, your privacy is our top priority. All personal information and health data are encrypted and stored securely. We comply with privacy regulations to ensure your data is kept confidential."
          },
        ]
      },
      {
        id: 3,
        title: "Payment & Subscription",
        items: [
          {
            title: "Are there any additional costs for using the platform?",
            text: "There are no hidden fees."
          },
          {
            title: "What payment methods are accepted?",
            text: "We accept payments through mobile money, Visa, Mastercard"
          },
        ]
      },
      {
        id: 4,
        title: "Monitoring & Results",
        items: [
          {
            title: "Can I share my results with my physician?",
            text: "Yes, you can share your results directly with your physician. Your results are also accessible by our medical team to provide continuous care and advice."
          },
          {
            title: "What should I do if I receive abnormal results?",
            text: "If your results show any abnormalities, you will be notified, and our care physicians will contact you to discuss the next steps and recommend any necessary treatment."
          }
        ]
      },
      {
        id: 5,
        title: "Support & Assistance",
        items: [
          {
            title: "How do I contact customer support?",
            text: "You can contact our customer support team via the “Contact us” section on the website or by emailing us at [info@go-checks.com]. We aim to respond within 24 hours."
          },
          {
            title: "What if I have a medical emergency?",
            text: "Our platform is designed for ongoing health management and not for emergency care. If you have a medical emergency, please contact your local emergency services immediately or visit the nearest healthcare facility."
          },
          {
            title: "Can I request a drug refill through the platform?",
            text: "Yes, you can order medication refills directly through your portal. Your care physician will approve the request, and you can receive your medication through our delivery services."
          }
        ]
      }
    ] as FAQCategory[];
  }

  /**
   * Saves a value to localStorage under a specified key.
   *
   * @param key - The key under which the value will be stored in localStorage.
   * @param value - to be stored.
   */
  saveToLocalStorage(key: string, value: string | (MedicalTest | Drug)[] | boolean | number[] | number): void {
    localStorage.setItem(key, JSON.stringify(value));
  }

  /**
   * Retrieves an item from localStorage and parses it into the specified type.
   *
   * @param key - under which the item is stored
   * @param defaultValue
   * @returns T - The parsed item from localStorage
   */
  getFromLocalStorage<T>(key: string, defaultValue: T | null = null): T {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  }

  /**
   * Removes an item from localStorage.
   *
   * @param key - item to remove from localStorage.
   */
  removeFromLocalStorage(key: string): void {
    localStorage.removeItem(key);
  }

  /**
   * Removes a saved medical test from localStorage.
   *
   * @param testid - id of medical test to remove from localStorage.
   */
  removeMedicalTestFromLocalStorageCart(testId: number): void {
    const storedCartMedicalTests = JSON.parse(localStorage.getItem('cartItems') || '[]');
    const updatedItems = storedCartMedicalTests.filter((item: any) => item.id !== testId);
    localStorage.setItem('cartItems', JSON.stringify(updatedItems));
    this.cartUpdatedSubject.next();
  }    

  /**
   * Clears all items from localStorage.
   */
  clear(): void {
    localStorage.clear();
  }

  /**
   * Gets the filter details
   * @return FilterDetails[] of drugs
   * 
   */
  getPharmacyFilterDetails(): FilterDetails[] {
    return [
      {
        title: 'Pain Relief',
        icon: 'fal fa-capsules',
      },
      {
        title: 'Antibiotics',
        icon: 'fal fa-prescription-bottle',
      },
      {
        title: 'Cold & Flu',
        icon: 'fal fa-lungs',
      },
      {
        title: 'Allergy Medications',
        icon: 'fal fa-allergies',
      },
      {
        title: 'Digestive Health',
        icon: 'fal fa-stomach',
      },
      {
        title: 'Vitamins & Supplements',
        icon: 'fal fa-pills',
      },
      // {
      //   title: 'Cardiovascular Medications',
      //   icon: 'fal fa-heartbeat',
      // },
      // {
      //   title: 'Diabetes Care',
      //   icon: 'fal fa-tint',
      // },
      // {
      //   title: 'Mental Health & Antidepressants',
      //   icon: 'fal fa-brain',
      // },
      {
        title: 'Skin Care & Dermatology',
        icon: 'fal fa-user-md',
      },
      // {
      //   title: 'Hormonal & Endocrine Therapy',
      //   icon: 'fal fa-vial',
      // },
      // {
      //   title: 'Respiratory Medications',
      //   icon: 'fal fa-lungs',
      // },
      {
        title: 'Eye & Ear Care',
        icon: 'fal fa-eye',
      },
      {
        title: 'First Aid & Wound Care',
        icon: 'fal fa-band-aid',
      },
      {
        title: 'Fertility & Reproductive Health',
        icon: 'fal fa-venus-mars',
      },
      {
        title: 'Sleep Aids & Relaxation',
        icon: 'fal fa-bed',
      },
      {
        title: 'Weight Management',
        icon: 'fal fa-weight',
      },
      // {
      //   title: 'Antifungal & Antiviral Drugs',
      //   icon: 'fal fa-disease',
      // },
      // {
      //   title: 'Smoking Cessation',
      //   icon: 'fal fa-smoking-ban',
      // },
      {
        title: 'Others',
        icon: 'fal fa-books-medical',
      }
    ];
  }

  /**
   * Gets the quantity name labels
   * @return QuantityDetails[] of names
   */
  getLocalQuantityNamesLabels(): QuantityDetails[] {
    return [
      {
        name: 'tablet'
      },
      {
        name: 'strip'
      },
      {
        name: 'box'
      },
      {
        name: 'can'
      },
      {
        name: 'bottle'
      },
      {
        name: 'tube'
      },
      {
        name: 'packet'
      },
      {
        name: 'capsule'
      },
      {
        name: 'sachet'
      },
      {
        name: 'tin'
      },
      {
        name: 'patches'
      },
      {
        name: 'ampoule/Vial'
      },
      { 
        name: 'Lozenge'
      },
      {
        name: 'other'
      }
    ] as QuantityDetails[];
  }

  /*
   * Gets the work process
   * @return CommonData[] of statuses
   * 
   */
  getLocalWorkProcess(): CommonData[] {
    return [
      {
        // "icon": "fal fa-shopping-cart",
        "title": "Order Online",
        "text": "Select your test from our catalogue, confirm your order easily and make a payment ."
      },
      {
        // "icon": "fal fa-vial",
        "title": "Sample Collection",
        "text": "Our medical team collects your sample at your home or workplace at your convenience."
      },
      {
        // "icon": "fal fa-file-medical-alt",
        "title": "Get Results",
        "text": "Receive test results quickly and accurately through your preferred communication method."
      },
      {
        // "icon": "fal fa-headset",
        "title": "Medical Support",
        "text": "Reach out to our support team for any medical inquiries or further assistance."
      }
    ] as CommonData[];
  }

  addProductToCart(product: MedicalTest | Drug, selectedType?: QuantityDetails) {
    this.authenticationService.isLoggedIn().subscribe(isLoggedIn => {
      if (!isLoggedIn) {
        this.router.navigate(['authenticate/login']);
        return;
      }
      let storedQuantities = this.getExistingLocalCartItemQuantities() || [];
      const existingIndex = storedQuantities.findIndex(
        item => item.productId === product.id
      );
      const isDrug = 'country_of_origin' in product;
      if (!isDrug) {
        if (existingIndex === -1) {
          const newItem: CartQuantityItem = {
            productId: product.id,
            quantity: 1
          };
          storedQuantities.push(newItem);
          this.productQuantity[product.id] = 1;
          this.addRemoveCartItem(product, this.cartItems);
        } else {
          delete this.productQuantity[product.id];
          storedQuantities.splice(existingIndex, 1);
          this.cartService.removeFromCart(product);
          this.removeMedicalTestFromLocalStorageCart(product.id);
        }
      } else {
        const existingItem = storedQuantities[existingIndex];
        const selectedTypeName = selectedType?.name;
        if (existingIndex === -1) {
          const newItem: CartQuantityItem = {
            productId: product.id,
            quantity: 1,
            quantity_type: selectedTypeName,
            amount: selectedType?.selling_price,
            quantityTypeId: selectedType?.id
          };
          storedQuantities.push(newItem);
          this.productQuantity[product.id] = 1;
          this.addRemoveCartItem(product, this.cartItems);
        } else {
          if (existingItem.quantity_type === selectedTypeName) {
            delete this.productQuantity[product.id];
            storedQuantities.splice(existingIndex, 1);
            this.cartService.removeFromCart(product);
            this.removeMedicalTestFromLocalStorageCart(product.id);
          } else {
            storedQuantities[existingIndex] = {
              ...existingItem,
              quantity_type: selectedTypeName,
              amount: selectedType?.selling_price
            };
            this.saveLocalCartItemQuantities(storedQuantities);
          }
        }
      }
      this.cartItemQuantitiesAdded = storedQuantities;
      this.saveLocalCartItemQuantities([...storedQuantities]);
    });
  }

  updateMedicalTestQuantities() {
    this.cartItemQuantitiesAdded = Object.entries(this.productQuantity).map(
      ([productId, quantity]) => ({
        productId: Number(productId),
        quantity: quantity as number,
      })
    );
    this.saveLocalCartItemQuantities([...this.cartItemQuantitiesAdded]);
  }

  /**
   * Gets the name of an item in the cart
   * @return item name
   * @param item - MedicalTest or Drug
   */
  getItemName(item: MedicalTest | Drug): string {
    return (item as Drug).name
      ? (item as Drug).name as string
      : (item as MedicalTest).name as string;
  }

  /**
   * Calculates the total cost of all items in the cart, including consultation if applicable.
   *
   * @param items - The list of items in the cart (MedicalTest or Drug).
   * @param itemQuantities - selected quantities.
   * @param itemAmounts - price for each drug in cart.
   * @param isDrug 
   * @param addConsultation 
   * @param consultationAmount - The cost of consultation to be added if applicable.
   * @returns The total cost as a number.
   */
  getCartTotalCost(
    options: CartCostOptions
  ): number {
    const {
      items,
      itemQuantities,
      itemAmounts,
      isDrug,
      addConsultation,
      consultationAmount,
      selectedQuantityTypeIds,
      quantities
    } = options;
    if ((!items || items.length === 0) && !addConsultation) {
      return 0;
    }
    let total = 0;
    for (let index = 0; index < items.length; index++) {
      const item = items[index];
      if (isDrug) {
        if (selectedQuantityTypeIds && quantities) {
          const quantityTypeId = selectedQuantityTypeIds[index];
          const quantity = quantities[index] || 1;
          const drug = item as Drug;
          const selectedQuantityType = drug.quantity_types?.find(qt => qt.id === quantityTypeId);
          const price = selectedQuantityType ? parseFloat(selectedQuantityType.selling_price || '0') : 0;
          total += price * quantity;
        } else {
          // Legacy fallback for older drug orders (without quantity types)
          const quantity = itemQuantities[item.id] || 1;
          const amount = parseFloat(itemAmounts[item.id] || '0');
          total += amount * quantity;
        }
      } else {
        const quantity = itemQuantities[item.id] || 1;
        const price = parseFloat((item as MedicalTest).price || '0');
        total += price * quantity;
      }
    }
    if (addConsultation) {
      total += consultationAmount;
    }
    return total;
  }

  refreshCartState(): {
    cartItems: (Drug | MedicalTest)[],
    quantities: any
  } {
    const cartItems = JSON.parse(localStorage.getItem('cartItems') || '[]');
    const quantities = JSON.parse(localStorage.getItem('cartItemQuantities') || '[]');
    return { cartItems, quantities };
  }

}
